import AngularTemplate from 'core/angular';
import {
  AdminAppLayout,
  DefaultLayout,
  DefaultLayoutFullHeight,
  DefaultLayoutModernized,
  FullScreenLayout,
  HomepageLayout,
} from 'core/layouts';
import DatabaseConfigs from 'modules/adminDatabaseConfigs';
import AdminEndpoints from 'modules/adminEndpoints';
import MetricDefinitions from 'modules/adminMetricDefinitions';
import { AllOrganizationsPage } from 'modules/allOrganizations';
import {
  AnonCriteriaCreatePage,
  AnonCriteriaDetailsPage,
  AnonCriteriaIndexPage,
} from 'modules/anonymousUserTracking';
import {
  AUA_BASE_PAGE_PATH,
  AUA_CREATE_PAGE_PATH,
  AUA_DETAILS_PAGE_PATH,
} from 'modules/anonymousUserTracking/constants';
import { ApiKeysPage } from 'modules/apiKeys';
import { AuthenticationPolicyPage } from 'modules/authenticationPolicy';
import { Billing } from 'modules/billing';
import BossError from 'modules/bossError';
import { CampaignAnalyticsContext as CampaignAnalytics } from 'modules/campaignAnalytics/context/CampaignAnalyticsContext';
import { CampaignFoldersContext as CampaignFoldersPage } from 'modules/campaignIndex';
import { Alerts, CampaignEditor } from 'modules/campaigns';
import { useCampaignIdFromUrlParams } from 'modules/campaigns/lib/hooks/useCampaignIdFromUrlParams';
import { CampaignsCalendarPage } from 'modules/campaignsCalendar/CampaignsCalendarPage';
import { CampaignsSentMessagesLogsPage } from 'modules/campaignsSentMessagesLogs';
import CollectionBuilder from 'modules/catalog/collectionBuilder';
import CatalogCollections from 'modules/catalog/collections';
import CatalogFieldTypes from 'modules/catalogFieldTypes';
import CatalogTable, { AllCatalogsTable } from 'modules/catalogTable';
import { ChannelsPage } from 'modules/channels';
import { ContactLookupPage } from 'modules/contactLookup';
import { IndexPageContext as CreativeLibraryPage } from 'modules/creativeLibrary';
import { CustomEventsPage } from 'modules/customEvents';
import Dashboard from 'modules/dashboard';
import { DataDictionaryPage, SCHEMA_MANAGEMENT_PREVIEW_KEY } from 'modules/dataDictionary';
import { DATA_SCHEMA_MANAGEMENT_URL } from 'modules/dataDictionary/constants';
import { DATA_SYNC_PREVIEW_KEY, DataSyncPages } from 'modules/dataSync';
import { DataFeedsPage } from 'modules/dataFeeds';
import {
  DATA_WAREHOUSE_PREVIEW_KEY,
  DATA_WAREHOUSE_URL,
  DataWarehousePage,
} from 'modules/dataWarehouse';
import { DemoData } from 'modules/demoData';
import { DEVICE_PREVIEW_KEY, DevicePreviewPage } from 'modules/devicePreview';
import { DnsSetup } from 'modules/dnsSetup';
import { EnabledAccounts } from 'modules/enabledAccounts';
import { ErrorBoundary, ErrorMarkup } from 'modules/error';
import { EventsLog } from 'modules/eventsLog';
import { ExperimentsIndexPage, ExperimentsMonitorPage } from 'modules/experiments';
import { FacebookIntegrations, SelectAdAccounts } from 'modules/facebookIntegrations';
import FeatureFlags from 'modules/featureFlags';
import { FrequencyManagementIndex } from 'modules/frequencyManagement';
import GlobalSmsSettings from 'modules/globalSms';
import { GoalsPage } from 'modules/goals/GoalsPage';
import { GOALS_PAGE_PREVIEW_KEY } from 'modules/goals/constants';
import { InboundSMS } from 'modules/inboundSMS';
import { IntelligentInsightsIndexPage } from 'modules/intelligentInsights/IntelligentInsightsIndexPage';
import { InternalMetrics } from 'modules/internalMetrics';
import { ENABLE_INTERNAL_TOOLS, INTERNAL_TOOLS_KEY, InternalTools } from 'modules/internalTools';
import { GoalAnalyticsContext as GoalAnalyticsPage } from 'modules/iterableGoals';
import { JOB_MANAGER_PREVIEW_KEY, JobManagerIndexPage } from 'modules/jobManager';
import { SegmentationPage as LegacySegmentationPage } from 'modules/legacySegmentation';
import { LinkExpirationManagementPage } from 'modules/linkExpirationManagement';
import {
  ENABLE_LIST_IMPORT_PAGE,
  LIST_IMPORT_PREVIEW_KEY,
  ListImportPage,
} from 'modules/listImport';
import { DeprecatedListIndexPage, ListIndexPage } from 'modules/listIndex';
import { MailDomains, Path, TrackingDomain } from 'modules/mailDomains';
import { MessagingInsightsContext as MessagingInsights } from 'modules/messagingInsights';
import { MissionControlHomePage } from 'modules/missionControl/MissionControlHomePage';
import { HOMEPAGE_PREVIEW_KEY } from 'modules/missionControl/constants';
import { AppDetails, AppsAndWebsites, CreateApp, EditApp } from 'modules/mobileApps';
import { MI_PREVIEW_KEY, MovableInkIntegrations } from 'modules/movableInk';
import { NewOrganizationPage } from 'modules/newOrganizationForm';
import {
  MemberDetailsPageModernized,
  MembersIndexPage,
  ProjectMembersPage,
} from 'modules/organization';
import { PlacementsCreate, PlacementsManage } from 'modules/placements';
import { GeneralError, PredictiveGoalsPages } from 'modules/predictiveGoals';
import { PROJECT_SETTINGS_MODERNIZATION, ProjectSettings } from 'modules/projectSettings';
import { Projects } from 'modules/projects';
import { Purchases } from 'modules/purchases';
import Report from 'modules/report';
import ReportsTable from 'modules/reportsTable';
import { Roles } from 'modules/roles';
import { SavedViewsIndex } from 'modules/savedViewsIndex';
import { ScheduledMessages } from 'modules/scheduledMessages';
import { MODERNIZED_SEGMENTATION_PREVIEW_KEY, SegmentationPage } from 'modules/segmentation';
import { SendersPage } from 'modules/senders';
import { SendingPlatformsDetailsPage, SendingPlatformsIndexPage } from 'modules/sendingPlatforms';
import { SentMessagePage } from 'modules/sentMessage';
import {
  SnippetEditor,
  SnippetContext as SnippetIndexPage,
  SnippetReferencesContext as SnippetReferences,
} from 'modules/snippets';
import { SystemWebhookEdit, SystemWebhooksPage } from 'modules/systemWebhooks';
import { TEMPLATE_DIFF_PREVIEW_KEY, TemplateDiffPage } from 'modules/templateDiff';
import { TemplateEditContext as TemplateEditor } from 'modules/templateEditor/TemplateEditContext';
import { TEMPLATE_HISTORY_PREVIEW_KEY, TemplateHistoryPage } from 'modules/templateHistory';
import ThirdPartyIntegrations from 'modules/thirdPartyIntegrations';
import { ToastContainer } from 'modules/toastContainer/components/ToastContainer';
import { UserPreferencesPage } from 'modules/userPreferences';
import { UserProfilesPage } from 'modules/userProfiles';
import { WebPush } from 'modules/webPush';
import { WhatsAppMessages } from 'modules/whatsappMessages';
import { WorkflowIndexPage } from 'modules/workflowIndex';
import { CreateWorkflowWebhookPage, WorkflowWebhookPage } from 'modules/workflowWebhooks';
import WorkflowWebhooksIndexPage from 'modules/workflowWebhooksIndex';
import { Workflow } from 'modules/workflows/Workflow';
import React, { PropsWithChildren } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { CompatRoute } from 'react-router-dom-v5-compat';
import { RumComponentContextProvider, addTeamContextToRUM } from 'services/observability/datadog';
import env from 'utils/env';
import { Product, Team } from './teams';

const TemplateFoldersPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TemplateFoldersPage" */ 'modules/templateIndex/templateFolders/TemplateFoldersContext'
    ),
);
const GraphiQL = React.lazy(() => import(/* webpackChunkName: "GraphiQL" */ 'modules/graphiql'));

const CAMPAIGN_TEAM_TEMPLATE_EDITOR_DD_TAG = {
  teamName: Team.CAMPAIGN_TEAM,
  productName: Product.TEMPLATE_EDITOR,
};

const render =
  <ComponentProps extends Record<string, any>>(
    Component: React.ComponentType<ComponentProps>,
    Layout: React.ComponentType<PropsWithChildren<any>> = DefaultLayout,
    componentProps?: ComponentProps,
  ) =>
  (
    teamName = Team.UNSPECIFIED,
    productName = Product.UNSPECIFIED,
    componentName = '',
    errorPage = <ErrorMarkup />,
  ) =>
  (propsFromRouter: any) => {
    if (env.isProdEnv) {
      addTeamContextToRUM({ name: teamName, product: productName });
    }

    return (
      <RumComponentContextProvider
        componentName={componentName || Component.name || ''}
        {...{ teamName, productName }}
      >
        <Layout>
          <React.Suspense fallback={<span />}>
            <ErrorBoundary {...{ teamName, productName }} errorMarkup={errorPage}>
              <Component {...propsFromRouter} {...componentProps} />
            </ErrorBoundary>
          </React.Suspense>
        </Layout>
        <ToastContainer />
      </RumComponentContextProvider>
    );
  };

const renderAngularPage = (props: any) =>
  render(AngularTemplate, DefaultLayout, {
    angularTemplateName: 'main',
    angularModuleName: 'iterableApp',
  })()(props);

const renderWorkflowRouter = (props: any) => {
  if (props?.location?.search?.includes('mode=beta')) {
    return render(Workflow, FullScreenLayout)(Team.JOURNEY_TEAM, Product.JOURNEY_STUDIO)(props);
  } else {
    return renderAngularPage(props);
  }
};

type GenerateRouterProps = {
  /** a list of the enabled preview strings on the current route */
  enabledPreviews: string[];
  /** the userContext's featureFlags */
  featureFlags: Record<string, boolean>;
  /** if the user has an iterable email */
  hasIterableEmail: boolean;
};

/**
 * Routes for the React app. If no routes in the React app match the current URL, the
 * <AngularTemplate /> component will be mounted to render the Angular app instead.
 *
 * Please help maintain this list of routes in alphabetical order :)
 *
 * Checklist
 *  - Does the route need to be added to QuickNav.js?
 */
export const generateRoutes = ({
  enabledPreviews = [],
  featureFlags = {},
  hasIterableEmail,
}: GenerateRouterProps) => {
  // if a feature flag is disabled, the route can be selectively enabled for employees
  // who include the key as a `previews` value in the URL
  const allowPreview = (flag: boolean, previewKey: string) =>
    (hasIterableEmail && enabledPreviews.includes(previewKey)) || flag;

  const receivedFeatureFlags = Object.keys(featureFlags).length > 0;

  return (
    <Switch>
      {allowPreview(featureFlags.MissionControlHomepage, HOMEPAGE_PREVIEW_KEY) && (
        <Route
          exact
          path="/"
          render={render(MissionControlHomePage, HomepageLayout)(Team.NOVA_TEAM, Product.HOMEPAGE)}
        />
      )}
      {allowPreview(
        featureFlags.MissionControlHomepage && featureFlags.MissionControlBeta,
        GOALS_PAGE_PREVIEW_KEY,
      ) && (
        <Route
          exact
          path="/goals"
          render={render(GoalsPage)(Team.NOVA_TEAM, Product.GOALS_INDEX)}
        />
      )}
      <Route exact path="/admin/endpoints" render={render(AdminEndpoints, AdminAppLayout)()} />
      <Route exact path="/admin/featureFlags" render={render(FeatureFlags, AdminAppLayout)()} />
      <Route
        exact
        path="/admin/metricDefinitions"
        render={render(MetricDefinitions, AdminAppLayout)()}
      />
      <Route
        exact
        path="/admin/databaseConfigs"
        render={render(DatabaseConfigs, AdminAppLayout)()}
      />
      {allowPreview(ENABLE_INTERNAL_TOOLS, INTERNAL_TOOLS_KEY) && (
        <Route exact path="/admin/internalTools" render={render(InternalTools, AdminAppLayout)()} />
      )}
      <Route exact path="/admin/manageDemo" render={render(DemoData)()} />
      <Route
        exact
        path="/admin/newOrganization"
        render={render(NewOrganizationPage)(Team.PLATFORM_TEAM, Product.ADMIN)}
      />
      <Route
        exact
        path="/analytics/campaignPerformance/:section?"
        render={render(CampaignAnalytics)(Team.ANALYTICS_TEAM, Product.CAMPAIGN_PERFORMANCE)}
      />
      {featureFlags.IterableGoals && (
        <Route
          exact
          path="/analytics/goalPerformance"
          render={render(GoalAnalyticsPage)(Team.EXPERIMENT_TEAM, Product.ITERABLE_GOALS)}
        />
      )}
      {
        <Route
          path={[
            '/intelligentInsights/brandAffinityInsights',
            '/intelligentInsights/sendTimeOptimizationInsights',
            '/intelligentInsights/froReporting',
          ]}
          render={render(IntelligentInsightsIndexPage, DefaultLayoutFullHeight)(
            Team.AI_TEAM,
            Product.BRAND_AFFINITY_INSIGHTS,
          )}
        />
      }
      {featureFlags.AssetManagerIndex && (
        <Route
          exact
          path="/creativeLibrary"
          render={render(CreativeLibraryPage)(Team.CONTENT_TEAM, Product.CREATIVE_LIBRARY)}
        />
      )}
      <Route exact path="/boss/error" render={render(BossError)()} />
      <Route
        exact
        path="/campaigns/manage"
        render={render(CampaignFoldersPage)(Team.CAMPAIGN_TEAM, Product.CAMPAIGN_INDEX)}
      />
      {featureFlags.CampaignReactificationV1 && (
        <Route
          exact
          path="/campaigns/:id(\d+)"
          render={render(CampaignEditor)(Team.CAMPAIGN_TEAM, Product.CAMPAIGN_EDITOR)}
        />
      )}
      {featureFlags.ReactTriggeredCampaignsSendMessageLogs && (
        <Route
          exact
          path="/campaigns/triggeredSendLog"
          render={render(CampaignsSentMessagesLogsPage)(
            Team.CAMPAIGN_TEAM,
            Product.CAMPAIGNS_SENT_MESSAGES_LOGS,
          )}
        />
      )}
      {featureFlags.ReactCampaignCalendar && (
        <Route
          exact
          path="/campaigns/calendar"
          render={render(CampaignsCalendarPage)(Team.CAMPAIGN_TEAM, Product.CAMPAIGN_CALENDAR)}
        />
      )}
      <Route
        exact
        path="/catalogs"
        render={render(AllCatalogsTable)(Team.AUDIENCE_TEAM, Product.CATALOG)}
      />
      <Route
        exact
        path="/catalogs/collections"
        render={render(CatalogCollections)(Team.AUDIENCE_TEAM, Product.CATALOG)}
      />
      <Route
        exact
        path="/catalogs/collections/:collectionId"
        render={render(CollectionBuilder)(Team.AUDIENCE_TEAM, Product.CATALOG)}
      />
      <Route
        exact
        path="/catalogs/table/:tableName"
        render={render(CatalogTable)(Team.AUDIENCE_TEAM, Product.CATALOG)}
      />
      <Route
        exact
        path="/catalogs/table/:tableName/fieldTypes"
        render={render(CatalogFieldTypes)(Team.AUDIENCE_TEAM, Product.CATALOG)}
      />
      {allowPreview(featureFlags.LinkExpirationManagement, 'link-expiration-management') && (
        <Route
          exact
          path="/settings/linkExpirationManagement"
          render={render(LinkExpirationManagementPage, DefaultLayoutFullHeight)(
            Team.PLATFORM_TEAM,
            Product.SETTINGS,
          )}
        />
      )}
      <Route
        exact
        path="/dashboard"
        render={render(Dashboard)(Team.ANALYTICS_TEAM, Product.DASHBOARD)}
      />
      <Route exact path="/events" render={render(EventsLog)()} />
      <Route
        exact
        path="/experiments"
        render={render(ExperimentsIndexPage)(Team.EXPERIMENT_TEAM, Product.EXPERIMENT_INDEX)}
      />
      <Route
        exact
        path="/experiments/monitor"
        render={render(ExperimentsMonitorPage)(Team.EXPERIMENT_TEAM, Product.EXPERIMENT_MONITOR)}
      />
      {featureFlags.FrequencyCappingMessageType && (
        <Route exact path="/settings/frequencyManagement">
          <Redirect to="/settings/frequencyManagement/email" />
        </Route>
      )}
      {featureFlags.FrequencyCappingMessageType && (
        <Route
          exact
          path="/settings/frequencyManagement/:messageMedium"
          render={render(FrequencyManagementIndex)(Team.PLATFORM_TEAM, Product.SETTINGS)}
        />
      )}
      <Route exact path="/i/metrics" render={render(InternalMetrics)()} />
      {allowPreview(featureFlags.EtlIntegration, DATA_SYNC_PREVIEW_KEY) && (
        <Route
          exact
          path="/integrations/dataSync"
          render={render(DataSyncPages)(Team.ANALYTICS_PLATFORM_TEAM, Product.ETL)}
        />
      )}
      {allowPreview(featureFlags.EtlIntegration, DATA_SYNC_PREVIEW_KEY) && (
        <Route
          exact
          path="/integrations/dataSync/create/:destinationType"
          render={render(DataSyncPages)(Team.ANALYTICS_PLATFORM_TEAM, Product.ETL)}
        />
      )}
      {allowPreview(featureFlags.EtlIntegration, DATA_SYNC_PREVIEW_KEY) && (
        <Route
          exact
          path="/integrations/dataSync/edit/:destinationId"
          render={render(DataSyncPages)(Team.ANALYTICS_PLATFORM_TEAM, Product.ETL)}
        />
      )}
      {allowPreview(featureFlags.EtlIntegration, DATA_SYNC_PREVIEW_KEY) && (
        <Route
          exact
          path="/integrations/dataSync/transferHistory/:destinationId"
          render={render(DataSyncPages)(Team.ANALYTICS_PLATFORM_TEAM, Product.ETL)}
        />
      )}
      <Route
        exact
        path="/integrations/dataFeeds"
        render={render(DataFeedsPage)(Team.CONTENT_TEAM, Product.DATA_FEEDS)}
      />
      <Route
        exact
        path="/integrations/facebookIntegration"
        render={render(FacebookIntegrations)(Team.AUDIENCE_TEAM, Product.FACEBOOK)}
      />
      <Route
        exact
        path="/integrations/selectAdAccounts"
        render={render(SelectAdAccounts)(Team.AUDIENCE_TEAM, Product.FACEBOOK)}
      />
      {allowPreview(featureFlags.MovableInk, MI_PREVIEW_KEY) && (
        <Route
          exact
          path="/integrations/movableInkIntegration"
          render={render(MovableInkIntegrations)(Team.CONTENT_TEAM, Product.MOVABLE_INK)}
        />
      )}
      <Route
        exact
        path="/integrations/webhooks"
        render={render(SystemWebhooksPage)(Team.PLATFORM_TEAM, Product.WEB_HOOK)}
      />
      <Route
        exact
        path="/integrations/webhooks/:webhookId/edit"
        render={render(SystemWebhookEdit)(Team.PLATFORM_TEAM, Product.WEB_HOOK)}
      />
      <Route
        exact
        path="/integrations/mobileApps"
        render={render(AppsAndWebsites)(Team.MOBILE_TEAM, Product.MOBILE_APPS)}
      />
      <Route
        exact
        path="/integrations/mobileApps/create"
        render={render(CreateApp)(Team.MOBILE_TEAM, Product.MOBILE_APPS)}
      />
      <Route
        exact
        path="/integrations/mobileApps/:id"
        render={render(AppDetails)(Team.MOBILE_TEAM, Product.MOBILE_APPS)}
      />
      <Route
        exact
        path="/integrations/mobileApps/:id/edit"
        render={render(EditApp)(Team.MOBILE_TEAM, Product.MOBILE_APPS)}
      />
      <Route exact path="/integrations/mobilePush">
        <Redirect to="/integrations/mobileApps" />
      </Route>
      <Route exact path="/integrations/thirdParty" render={render(ThirdPartyIntegrations)()} />
      <Route
        exact
        path="/integrations/webPush"
        render={render(WebPush)(Team.MOBILE_TEAM, Product.WEB_PUSH)}
      />
      {allowPreview(featureFlags.JobManager, JOB_MANAGER_PREVIEW_KEY) && (
        <Route
          exact
          path="/jobs/manage"
          render={render(JobManagerIndexPage)(Team.ECOSYSTEM_TEAM, Product.JOB_MANAGER)}
        />
      )}
      {(allowPreview(ENABLE_LIST_IMPORT_PAGE, LIST_IMPORT_PREVIEW_KEY) ||
        featureFlags.ModernizedListImportPageUI) && (
        <Route
          exact
          path="/lists/import"
          render={render(ListImportPage)(Team.AUDIENCE_TEAM, Product.LISTS)}
        />
      )}
      <Route
        exact
        path="/lists"
        render={render(
          featureFlags.ListIndexModernizationUI ? ListIndexPage : DeprecatedListIndexPage,
        )(Team.AUDIENCE_TEAM, Product.LISTS)}
      />
      <Route
        exact
        path="/members"
        render={render(MembersIndexPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/members/:userId"
        render={render(MemberDetailsPageModernized)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path={[
          '/payments/info',
          '/payments/info/dataUsage',
          '/payments/info/journeyEventsUsage',
          '/payments/info/journeyEventsUsage/:projectId',
          '/payments/info/audienceUsage',
          '/payments/info/catalogUsage',
          '/payments/info/messagingUsage',
          '/payments/info/smartIngestUsage',
        ]}
        render={render(
          Billing,
          allowPreview(featureFlags.BillingUsageLimits, 'billing-usage-limits')
            ? DefaultLayoutModernized
            : DefaultLayout,
        )(Team.PLATFORM_TEAM, Product.PAYMENTS)}
      />
      <Route
        exact
        path="/preferences"
        render={render(UserPreferencesPage)(Team.PLATFORM_TEAM, Product.PREFERENCES)}
      />
      <Route
        exact
        path="/messagingInsights"
        render={render(MessagingInsights)(Team.ANALYTICS_TEAM, Product.MESSAGING_INSIGHTS)}
      />
      <Route
        exact
        path="/messagingInsights/views/:viewId"
        render={render(MessagingInsights)(Team.ANALYTICS_TEAM, Product.MESSAGING_INSIGHTS)}
      />
      {allowPreview(featureFlags.HightouchIntegration, DATA_WAREHOUSE_PREVIEW_KEY) && (
        <Route
          exact
          path={DATA_WAREHOUSE_URL}
          render={render(DataWarehousePage, DefaultLayoutFullHeight)(
            Team.PLATFORM_TEAM,
            Product.SETTINGS,
          )}
        />
      )}
      <Route
        exact
        path="/savedViews"
        render={render(SavedViewsIndex)(Team.ANALYTICS_TEAM, Product.SAVED_VIEWS)}
      />
      <Route exact path="/projects/:projectId" render={render(ProjectMembersPage)()} />
      <Route exact path="/purchases" render={render(Purchases)()} />
      {(featureFlags.WhatsAppMessageChannel || featureFlags.WhatsAppMessageChannelGA) && (
        <Route exact path="/whatsAppMessages" render={render(WhatsAppMessages)()} />
      )}
      <Route
        exact
        path="/reports"
        render={render(ReportsTable)(Team.ANALYTICS_TEAM, Product.REPORTS)}
      />
      <Route path="/reports/:id" render={render(Report)(Team.ANALYTICS_TEAM, Product.REPORTS)} />
      <Route
        exact
        path={['/roles', '/roles/:roleId']}
        render={render(Roles)(Team.PLATFORM_TEAM, Product.ROLES)}
      />
      {featureFlags.CampaignPerformanceAlerts && (
        <Route
          exact
          path="/settings/alerts/campaigns"
          render={render(Alerts)(Team.CAMPAIGN_TEAM, Product.CAMPAIGN_PROJECT_ALERTS)}
        />
      )}
      <Route exact path="/settings/allEnabledAccounts" render={render(EnabledAccounts)()} />
      {featureFlags.PredictiveGoals && (
        <CompatRoute
          path="/predictive-audiences"
          render={render(PredictiveGoalsPages, DefaultLayoutFullHeight)(
            Team.AI_TEAM,
            Product.PRG,
            '',
            <GeneralError />,
          )}
        />
      )}
      <Route
        exact
        path="/segmentation"
        render={
          allowPreview(featureFlags.ModernizedSegmentationPage, MODERNIZED_SEGMENTATION_PREVIEW_KEY)
            ? render(SegmentationPage, DefaultLayoutFullHeight)(
                Team.AUDIENCE_TEAM,
                Product.SEGMENTATION,
              )
            : render(LegacySegmentationPage)(Team.AUDIENCE_TEAM, Product.SEGMENTATION)
        }
      />
      <Route
        exact
        path="/settings/senders"
        render={render(SendersPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/settings/sendingPlatforms"
        render={render(SendingPlatformsIndexPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        // @ts-ignore - For some reason, this is using `from` (which not a known property for `Route` per v5 docs) instead of `path`, and it works. Leaving it as is for now.
        from="/settings/sendingPlatforms/:sendingPlatform"
        render={render(SendingPlatformsDetailsPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/settings/apiKeys"
        render={render(ApiKeysPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      {/* Render either Custom Events or Data Dictionary pages based on feature flag */}
      <Route exact path="/settings/customEvents">
        {allowPreview(featureFlags.SchemaManagementUI, SCHEMA_MANAGEMENT_PREVIEW_KEY) ? (
          <Redirect to={DATA_SCHEMA_MANAGEMENT_URL} />
        ) : (
          render(CustomEventsPage)(Team.PLATFORM_TEAM, Product.SETTINGS)
        )}
      </Route>
      {/* Render either Custom Events or Data Dictionary pages based on feature flag */}
      <Route
        exact
        path={[
          DATA_SCHEMA_MANAGEMENT_URL,
          `${DATA_SCHEMA_MANAGEMENT_URL}/customEvent/:customEventName`,
        ]}
      >
        {allowPreview(featureFlags.SchemaManagementUI, SCHEMA_MANAGEMENT_PREVIEW_KEY) ? (
          render(DataDictionaryPage, DefaultLayoutFullHeight)(Team.PLATFORM_TEAM, Product.SETTINGS)
        ) : (
          <Redirect to="/settings/customEvents" />
        )}
      </Route>
      <Route exact path="/settings/dnsSetup" render={render(DnsSetup)(Team.PLATFORM_TEAM)} />
      <Route exact path="/settings/orgs" render={render(AllOrganizationsPage)()} />
      {allowPreview(featureFlags.ProjectSettingsModernization, PROJECT_SETTINGS_MODERNIZATION) && (
        <Route
          exact
          path="/settings/project"
          render={render(ProjectSettings, DefaultLayoutModernized)(Team.PLATFORM_TEAM)}
        />
      )}
      <Route exact path="/settings/projects" render={render(Projects)()} />
      {featureFlags.GlobalSmsFirstPartyUI && (
        <Route
          exact
          path="/settings/project/globalSms"
          render={render(GlobalSmsSettings)(Team.MOBILE_TEAM, Product.SETTINGS)}
        />
      )}
      <Route
        exact
        path="/settings/authentication"
        render={render(AuthenticationPolicyPage)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/settings/domains"
        render={render(MailDomains)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/settings/domains/:trackingDomainName"
        render={render(TrackingDomain)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route
        exact
        path="/settings/domains/:trackingDomainName/:pathName"
        render={render(Path)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      <Route exact path="/settings/channels">
        <Redirect to="/settings/channels/email" />
      </Route>
      <Route
        exact
        path="/settings/channels/:messageMedium"
        render={render(ChannelsPage, DefaultLayoutFullHeight)(Team.PLATFORM_TEAM, Product.SETTINGS)}
      />
      {featureFlags.EmbeddedMessagingCore && (
        <Route
          exact
          path="/settings/placements"
          render={render(PlacementsManage)(Team.MOBILE_TEAM, Product.EMBEDDED_PLACEMENTS)}
        />
      )}
      {featureFlags.EmbeddedMessagingCore && (
        <Route
          exact
          path="/settings/placements/create"
          render={render(PlacementsCreate)(Team.MOBILE_TEAM, Product.EMBEDDED_PLACEMENTS)}
        />
      )}
      <Route
        exact
        path="/templates"
        render={render(TemplateFoldersPage)(Team.CAMPAIGN_TEAM, Product.TEMPLATE_INDEX)}
      />
      <Route
        exact
        path="/templatesCampaign"
        render={render(() => {
          const campaignId = useCampaignIdFromUrlParams();
          return <Redirect to={`/campaigns/${campaignId}`} />;
        })(Team.CAMPAIGN_TEAM, Product.TEMPLATE_SELECTOR)}
      />
      {(featureFlags.TemplateEditEmail ||
        featureFlags.TemplateEditInApp ||
        featureFlags.TemplateEditPush ||
        featureFlags.TemplateEditSMS ||
        featureFlags.TemplateEditWebPush) && (
        <Route exact path="/templates/editor">
          <RumComponentContextProvider
            componentName="templateEditor"
            {...CAMPAIGN_TEAM_TEMPLATE_EDITOR_DD_TAG}
          >
            <ErrorBoundary {...CAMPAIGN_TEAM_TEMPLATE_EDITOR_DD_TAG}>
              {/* @ts-ignore - some type errors here. Not fixing as part of this PR */}
              <TemplateEditor />
              <ToastContainer />
            </ErrorBoundary>
          </RumComponentContextProvider>
        </Route>
      )}
      {allowPreview(featureFlags.ReactTemplateDiff, TEMPLATE_DIFF_PREVIEW_KEY) && (
        <Route exact path="/templates/diff" render={render(TemplateDiffPage)(Team.CAMPAIGN_TEAM)} />
      )}
      {allowPreview(featureFlags.ReactTemplateHistory, TEMPLATE_HISTORY_PREVIEW_KEY) && (
        <Route
          exact
          path="/templates/history"
          render={render(TemplateHistoryPage)(Team.CAMPAIGN_TEAM)}
        />
      )}
      <Route
        exact
        path="/templates/snippets"
        render={render(SnippetIndexPage)(Team.CONTENT_TEAM, Product.SNIPPETS)}
      />
      <Route
        exact
        path="/templates/snippet/:id/edit"
        render={render(SnippetEditor)(Team.CONTENT_TEAM, Product.SNIPPETS)}
      />
      <Route
        exact
        path="/templates/snippet/create"
        render={render(SnippetEditor)(Team.CONTENT_TEAM, Product.SNIPPETS)}
      />
      <Route
        exact
        path="/templates/snippet/:id/locations"
        render={render(SnippetReferences)(Team.CONTENT_TEAM, Product.SNIPPETS)}
      />
      {allowPreview(featureFlags.ReactDevicePreview, DEVICE_PREVIEW_KEY) && (
        <Route
          exact
          path="/templates/devicePreview"
          render={render(DevicePreviewPage)(Team.CAMPAIGN_TEAM)}
        />
      )}
      <Route
        exact
        path="/triggered/scheduledMessages"
        render={render(ScheduledMessages)(Team.PLATFORM_TEAM, Product.TRIGGERS)}
      />
      <Route
        exact
        path="/triggered/viewSentEmail"
        render={render(SentMessagePage)(Team.PLATFORM_TEAM, Product.TRIGGERS)}
      />
      {featureFlags.AnonymousUserCriteria && (
        <Route
          exact
          path={AUA_BASE_PAGE_PATH}
          render={render(AnonCriteriaIndexPage, DefaultLayoutFullHeight)(
            Team.MOBILE_TEAM,
            Product.ANONYMOUS_USER_TRACKING,
          )}
        />
      )}
      {featureFlags.AnonymousUserCriteria && (
        <Route
          exact
          path={AUA_CREATE_PAGE_PATH}
          render={render(AnonCriteriaCreatePage)(Team.MOBILE_TEAM, Product.ANONYMOUS_USER_TRACKING)}
        />
      )}
      {featureFlags.AnonymousUserCriteria && (
        <Route
          exact
          path={AUA_DETAILS_PAGE_PATH(':id')}
          render={render(AnonCriteriaDetailsPage, DefaultLayoutFullHeight)(
            Team.MOBILE_TEAM,
            Product.ANONYMOUS_USER_TRACKING,
          )}
        />
      )}
      <Route exact path="/users/inboundSMS" render={render(InboundSMS)()} />
      <CompatRoute
        path="/users/lookup"
        render={render(ContactLookupPage)(Team.AUDIENCE_TEAM, Product.CONTACT_LOOKUP)}
      />
      <CompatRoute
        path="/users/profiles/:userKey"
        render={render(UserProfilesPage)(Team.AUDIENCE_TEAM, Product.USER_PROFILE)}
      />
      <Route
        exact
        path={['/workflows/create', '/workflows/:workflowId/edit']}
        render={renderWorkflowRouter}
      />
      <Route
        exact
        path="/workflows"
        render={render(WorkflowIndexPage)(Team.JOURNEY_TEAM, Product.JOURNEY_INDEX)}
      />
      <Route
        exact
        path="/workflows/webhooks"
        render={render(WorkflowWebhooksIndexPage)(Team.PLATFORM_TEAM, Product.WEB_HOOK)}
      />
      <Route
        exact
        path="/workflows/webhooks/create"
        render={render(CreateWorkflowWebhookPage)(Team.PLATFORM_TEAM, Product.WEB_HOOK)}
      />
      <Route
        exact
        path="/workflows/webhooks/:id"
        render={render(WorkflowWebhookPage)(Team.PLATFORM_TEAM, Product.WEB_HOOK)}
      />
      {process.env.NODE_ENV === 'development' && (
        <Route
          exact
          path="/graphiql"
          render={render(GraphiQL, FullScreenLayout)(
            Team.CONTENT_AND_JOURNEYS_TEAM,
            Product.GRAPHIQL,
          )}
        />
      )}
      {receivedFeatureFlags && <Route render={renderAngularPage} />}
    </Switch>
  );
};
